// Simple service worker manager without workbox dependency
class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateAvailable: boolean = false;
  private offlineReady: boolean = false;
  private syncQueues: Set<string> = new Set();

  async register() {
    if ('serviceWorker' in navigator) {
      try {
        // Register the service worker
        this.registration = await navigator.serviceWorker.register('/service-worker.js');
        
        // Register background sync
        if ('sync' in this.registration) {
          await Promise.all([
            this.registration.sync.register('messages-sync'),
            this.registration.sync.register('maintenance-sync'),
            this.registration.sync.register('documents-sync')
          ]);
        }

        // Register periodic sync if supported
        if ('periodicSync' in this.registration) {
          await this.registration.periodicSync.register('cache-cleanup', {
            minInterval: 24 * 60 * 60 * 1000 // 24 hours
          });
        }

        // Add event listeners
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (this.updateAvailable) {
            window.location.reload();
          }
        });

        // Listen for messages
        navigator.serviceWorker.addEventListener('message', this.handleMessage);

        // Check for updates
        if (this.registration.waiting) {
          this.updateAvailable = true;
          window.dispatchEvent(new CustomEvent('swUpdate'));
        }

        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration?.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.updateAvailable = true;
                window.dispatchEvent(new CustomEvent('swUpdate'));
              }
            });
          }
        });

        // Check offline status
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);

        return this.registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    }
  }

  private handleOnline = () => {
    window.dispatchEvent(new CustomEvent('online'));
    this.syncQueues.forEach(queue => {
      this.registration?.sync.register(queue).catch(console.error);
    });
  };

  private handleOffline = () => {
    window.dispatchEvent(new CustomEvent('offline'));
  };

  private handleMessage = (event: MessageEvent) => {
    try {
      const { type, payload } = event.data;
      
      switch (type) {
        case 'CACHE_UPDATED':
          console.log('Cache updated', payload);
          break;
        case 'OFFLINE_READY':
          this.offlineReady = true;
          console.log('Application ready for offline use');
          window.dispatchEvent(new CustomEvent('offlineReady'));
          break;
        case 'ERROR':
          console.error('Service Worker error', payload);
          break;
        default:
          console.log('Unknown message from Service Worker', type, payload);
      }
    } catch (error) {
      console.error('Error handling Service Worker message', error);
    }
  };

  async queueSync(queue: string, data: any) {
    if (!this.registration) return;

    this.syncQueues.add(queue);
    
    if (navigator.onLine) {
      await this.registration.sync.register(queue);
    } else {
      const cache = await caches.open(`${queue}-queue`);
      await cache.put(new Request(queue), new Response(JSON.stringify(data)));
    }
  }

  isOfflineReady(): boolean {
    return this.offlineReady;
  }

  async update() {
    if (this.registration) {
      try {
        await this.registration.update();
        console.log('Service Worker updated');
      } catch (error) {
        console.error('Service Worker update failed:', error);
        throw error;
      }
    }
  }

  async unregister() {
    if (this.registration) {
      try {
        // Cleanup sync registrations
        this.syncQueues.clear();
        if ('periodicSync' in this.registration) {
          await this.registration.periodicSync.unregister('cache-cleanup');
        }

        await this.registration.unregister();
        console.log('Service Worker unregistered');
      } catch (error) {
        console.error('Service Worker unregistration failed:', error);
        throw error;
      }
    }
  }

  skipWaiting() {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  async checkForUpdates() {
    if (this.registration) {
      try {
        await this.registration.update();
        if (this.registration.waiting) {
          this.updateAvailable = true;
          window.dispatchEvent(new CustomEvent('swUpdate'));
        }
      } catch (error) {
        console.error('Service Worker update check failed:', error);
      }
    }
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }
}

export const swManager = new ServiceWorkerManager();